import type {
  CompanyRefInfo,
  GeoRefInfo,
  RefInfo,
  RicsRefInfo,
  SkillRefInfo,
} from '@revelio/data-access';
import type { SelectionListIdNames } from '@revelio/iso-utility';

type SelectionListColumn<TItem> = Array<TItem | null> | null;

// The "GQL" selection-list vocabulary — RICS, geography, demographics and the
// (V2) skill granularities. RICS / geo / demographics are assembled at runtime
// in useSelectionListsForKeys (taxonomyTree + reference lists); skill data is
// sourced per surface (DataBuilder/api/useSkillSelectionLists, or the CustomDash
// custom-skill tree) rather than from a shared query. This hand-written type is
// the contract those items conform to — it deliberately no longer derives from a
// GraphQL query.
type GqlSelectionLists = {
  region?: SelectionListColumn<
    Pick<GeoRefInfo, '__typename' | 'id' | 'shortName' | 'longName'>
  >;
  country?: SelectionListColumn<
    Pick<GeoRefInfo, '__typename' | 'id' | 'shortName' | 'longName' | 'region'>
  >;
  state?: SelectionListColumn<
    Pick<GeoRefInfo, '__typename' | 'id' | 'shortName' | 'longName' | 'country'>
  >;
  msa?: SelectionListColumn<
    Pick<
      GeoRefInfo,
      '__typename' | 'id' | 'shortName' | 'longName' | 'region' | 'country'
    >
  >;
  // `GeoRefInfo` has no `county` field — a county's parent FK is its `state`.
  county?: SelectionListColumn<
    Pick<
      GeoRefInfo,
      | '__typename'
      | 'id'
      | 'shortName'
      | 'longName'
      | 'region'
      | 'country'
      | 'state'
    >
  >;
  metro_area?: SelectionListColumn<
    Pick<
      GeoRefInfo,
      | '__typename'
      | 'id'
      | 'shortName'
      | 'longName'
      | 'region'
      | 'country'
      | 'state'
    >
  >;
  city?: SelectionListColumn<
    Pick<
      GeoRefInfo,
      | '__typename'
      | 'id'
      | 'shortName'
      | 'longName'
      | 'region'
      | 'country'
      | 'state'
      | 'metro_area'
      | 'count'
    >
  >;
  rics_k10?: SelectionListColumn<
    Pick<
      RicsRefInfo,
      '__typename' | 'id' | 'shortName' | 'longName' | 'headcount'
    >
  >;
  rics_k50?: SelectionListColumn<
    Pick<
      RicsRefInfo,
      '__typename' | 'id' | 'shortName' | 'longName' | 'rics_k10' | 'headcount'
    >
  >;
  rics_k400?: SelectionListColumn<
    Pick<
      RicsRefInfo,
      | '__typename'
      | 'id'
      | 'shortName'
      | 'longName'
      | 'rics_k10'
      | 'rics_k50'
      | 'headcount'
    >
  >;
  company?: SelectionListColumn<
    Pick<
      CompanyRefInfo,
      | '__typename'
      | 'id'
      | 'shortName'
      | 'rank'
      | 'ticker'
      | 'longName'
      | 'headcount'
    > & {
      // Aliased in the source query from ricsK50Id / ricsK400Id.
      rics_k50?: CompanyRefInfo['ricsK50Id'];
      rics_k400?: CompanyRefInfo['ricsK400Id'];
    }
  >;
  seniority?: SelectionListColumn<
    Pick<RefInfo, '__typename' | 'id' | 'shortName' | 'longName'>
  >;
  ethnicity?: SelectionListColumn<
    Pick<RefInfo, '__typename' | 'id' | 'shortName' | 'longName'>
  >;
  gender?: SelectionListColumn<
    Pick<RefInfo, '__typename' | 'id' | 'shortName' | 'longName'>
  >;
  highest_degree?: SelectionListColumn<
    Pick<RefInfo, '__typename' | 'id' | 'shortName' | 'longName'>
  >;
  employment_relationship?: SelectionListColumn<
    Pick<RefInfo, '__typename' | 'id' | 'shortName' | 'longName'>
  >;
  work_schedule?: SelectionListColumn<
    Pick<RefInfo, '__typename' | 'id' | 'shortName' | 'longName'>
  >;
  remote_type?: SelectionListColumn<
    Pick<RefInfo, '__typename' | 'id' | 'shortName' | 'longName'>
  >;
  skill_k75?: SelectionListColumn<
    Pick<
      SkillRefInfo,
      '__typename' | 'id' | 'shortName' | 'longName' | 'topSkills'
    >
  >;
  skill_k700?: SelectionListColumn<
    Pick<
      SkillRefInfo,
      '__typename' | 'id' | 'shortName' | 'longName' | 'skill_k75' | 'topSkills'
    >
  >;
  skill_k3000?: SelectionListColumn<
    Pick<
      SkillRefInfo,
      | '__typename'
      | 'id'
      | 'shortName'
      | 'longName'
      | 'skill_k75'
      | 'skill_k700'
      | 'topSkills'
    >
  >;
};

// `metro_area` is an alias of `msa` kept for legacy callers; the "standard"
// keys exclude it.
type StandardSelectionLists = Omit<GqlSelectionLists, 'metro_area'>;

// The role selection-list vocabulary, sourced at runtime from the role
// TaxonomyTree (treeToRoleSelectionLists) rather than a GraphQL query. Each
// item carries its display names + the ancestor-level ids it descends from
// (parent-linked by level key). Like GqlSelectionLists, this hand-written type
// is the contract those items conform to and deliberately no longer derives
// from a query.
type RoleSelectionListItem = {
  __typename?: 'RoleTaxonomyRefInfo';
  id?: string | null;
  shortName?: string | null;
  longName?: string | null;
  nodeCount?: number;
  rawTitles?: string[];
  role_k7?: string | null;
  role_k50?: string | null;
  role_k150?: string | null;
  role_k500?: string | null;
  role_k1500?: string | null;
  role_k5000?: string | null;
};

export type RoleSelectionLists = {
  role_k7?: SelectionListColumn<RoleSelectionListItem>;
  role_k50?: SelectionListColumn<RoleSelectionListItem>;
  role_k150?: SelectionListColumn<RoleSelectionListItem>;
  role_k500?: SelectionListColumn<RoleSelectionListItem>;
  role_k1500?: SelectionListColumn<RoleSelectionListItem>;
  role_k5000?: SelectionListColumn<RoleSelectionListItem>;
  role_k15000?: SelectionListColumn<RoleSelectionListItem>;
};

type AllSelectionLists = GqlSelectionLists & RoleSelectionLists;

// Selection list keys
export type StandardSelectionListKey = keyof StandardSelectionLists;
export type GqlSelectionListKey = keyof GqlSelectionLists;

export type RoleSelectionListKey = keyof RoleSelectionLists;
// The custom-skill taxonomy levelId vocabulary, keyed positionally off the
// skill taxonomy tree's levels.
export type SkillTaxonomySelectionListKey =
  | 'skill_0'
  | 'skill_1'
  | 'skill_2'
  | 'skill_3'
  | 'skill_4';

/**
 * The full activity level-id vocabulary (k15 → k5000). Wider than
 * `ACTIVITY_TAXONOMY_SELECTION_LIST_KEYS` (the tiers the picker exposes) on
 * purpose: the wire mapping in role-skill-ent-kind covers every tier a
 * selection could carry.
 */
export type ActivityTaxonomySelectionListKey =
  | 'activity_k15'
  | 'activity_k50'
  | 'activity_k150'
  | 'activity_k500'
  | 'activity_k1500'
  | 'activity_k5000';

export type SelectionListKey = GqlSelectionListKey | RoleSelectionListKey;
export type AllSelectionListsWithSkillTaxonomyKey =
  | SelectionListKey
  | SkillTaxonomySelectionListKey
  | ActivityTaxonomySelectionListKey;

// Selection list items and state
export type SelectionListItem<T extends SelectionListKey = SelectionListKey> =
  NonNullable<NonNullable<AllSelectionLists[T]>[number]>;

export type SelectionList<T extends SelectionListKey> = SelectionListItem<T>[];

type RemoveNullFromProps<T> = {
  [K in keyof T]: Exclude<T[K], null>;
};

export type NormalizedSelectionListItem<
  T extends SelectionListKey = SelectionListKey,
> = Omit<RemoveNullFromProps<SelectionListItem<T>>, 'id'> & {
  id: string;
  [key: string]: unknown;
};

export type SelectionListItems<T extends SelectionListKey = SelectionListKey> =
  {
    id: SelectionListIdNames;
    value: NormalizedSelectionListItem<T>[];
  };

export type SelectionListState<T extends SelectionListKey = SelectionListKey> =
  Partial<{ [key in T]: SelectionList<key> }>;

// Key definitions
const standardGqlSelectionListKeysRecord: Record<
  StandardSelectionListKey,
  true
> = {
  region: true,
  country: true,
  state: true,
  msa: true,
  city: true,
  county: true,
  rics_k10: true,
  rics_k50: true,
  rics_k400: true,
  company: true,
  seniority: true,
  ethnicity: true,
  gender: true,
  highest_degree: true,
  employment_relationship: true,
  work_schedule: true,
  remote_type: true,
  skill_k75: true,
  skill_k700: true,
  skill_k3000: true,
};

export const standardGqlSelectionListKeys = Object.keys(
  standardGqlSelectionListKeysRecord
) as StandardSelectionListKey[];

const gqlSelectionListKeysRecord: Record<GqlSelectionListKey, true> = {
  ...standardGqlSelectionListKeysRecord,
  metro_area: true,
};

export const gqlSelectionListKeys = Object.keys(
  gqlSelectionListKeysRecord
) as GqlSelectionListKey[];

const roleSelectionListKeysRecord: Record<RoleSelectionListKey, true> = {
  role_k7: true,
  role_k50: true,
  role_k150: true,
  role_k500: true,
  role_k1500: true,
  role_k5000: true,
  role_k15000: true,
};

export const roleSelectionListKeys = Object.keys(
  roleSelectionListKeysRecord
) as RoleSelectionListKey[];

export const SKILL_TAXONOMY_SELECTION_LIST_KEYS: readonly SkillTaxonomySelectionListKey[] =
  ['skill_0', 'skill_1', 'skill_2', 'skill_3'] as const;

export const ACTIVITY_TAXONOMY_SELECTION_LIST_KEYS = [
  'activity_k150',
  'activity_k1500',
  'activity_k5000',
] as const;
